import { forwardRef, useMemo } from 'react';

import { withEventCatcher } from '@repo/event-processor';
import {
  IconEventWrapper,
  IconSwitcher,
  SpinnerWrapper,
} from '@src/components/Icon';
import ChevronRightIcon from '@src/components/Icon/ChevronRightIcon';
import LoadingIcon from '@src/components/Icon/LoadingIcon';
import { useNewThemeActive } from '@src/theming';

import BaseButton from '../BaseButton/BaseButton';

import * as newTheme from './newTheme';
import {
  Content,
  Label,
  Caret,
  InputContent,
  ButtonContainer,
  IconWrapper,
} from './styles';
import { primaryTheme } from './theme';
import { ButtonProps, ButtonTheme } from './types';

const getNewTheme = (
  themeName: ButtonTheme['name']
): Record<string, string> => {
  switch (themeName) {
    case 'secondary':
      return newTheme.secondaryTheme;
    case 'ghost':
      return newTheme.ghostTheme;
    case 'header':
      return newTheme.editorHeaderTheme;
    case 'editorMenuItem':
      return newTheme.editorMenuItemTheme;
    case 'primaryDark':
      return newTheme.primaryDarkTheme;
    case 'primary':
    default:
      return newTheme.primaryTheme;
  }
};

const getHeight = (height?: string, themeHeight?: string): string => {
  if (height) {
    return height;
  }
  if (themeHeight && typeof themeHeight === 'string') {
    return themeHeight;
  }
  return '32px'; // default
};

/**
 * @deprecated Use `@repo/ui-kit` instead.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      label,
      icon,
      loading,
      appendIcon = false,
      width = '122px',
      height,
      hasCaret = false,
      isCentered = true,
      isActive = false,
      disabled = false,
      theme,
      onClick,
      type,
      as,
      dataTestId,
      href,
      target,
      style,
      ariaLabel,
      ariaPressed,
      tabIndex,
      className,
      tooltip,
      onMouseEnter,
      onMouseLeave,
    },
    ref
  ) => {
    if (loading) {
      icon = { component: LoadingIcon, height: icon?.height };
      disabled = true;
    }

    let _theme = useMemo(() => ({ ...primaryTheme, ...theme }), [theme]);
    const iconHeight = icon?.height ?? _theme.iconHeight;

    const isNewThemeActive = useNewThemeActive();

    if (isNewThemeActive && _theme.name) {
      _theme = { ..._theme, ...getNewTheme(_theme.name) };
    }

    const renderCaret = (): JSX.Element => (
      <Caret>
        <ChevronRightIcon height="7px" isActive={isActive} theme={_theme} />
      </Caret>
    );

    const renderIcon = (): JSX.Element | null =>
      icon ? (
        <IconWrapper $theme={_theme}>
          <SpinnerWrapper theme={_theme}>
            <IconSwitcher
              disabled={disabled}
              height={iconHeight as string | undefined}
              icon={icon.component}
              isActive={!disabled && isActive}
              theme={{
                ..._theme,
                color: _theme.iconColor || _theme.color,
                disabledColor: _theme.iconDisabledColor || _theme.disabledColor,
                activeColor: _theme.iconActiveColor || _theme.activeColor,
                activeDefaultColor:
                  _theme.iconActiveDefaultColor || _theme.activeColor,
                secondaryColor:
                  _theme.iconSecondaryColor || _theme.secondaryColor,
                secondaryActiveColor: _theme.iconSecondaryActiveColor,
              }}
            />
          </SpinnerWrapper>
        </IconWrapper>
      ) : null;

    // props to pass to styled components
    const styleProps = {
      $disabled: disabled,
      $loading: loading,
      $isCentered: isCentered,
      $isActive: isActive,
      $theme: _theme,
    };

    return (
      <BaseButton
        ref={ref}
        ariaLabel={ariaLabel}
        ariaPressed={ariaPressed}
        as={as}
        className={className}
        dataTestId={dataTestId}
        disabled={disabled}
        height={getHeight(height, _theme.height)}
        href={disabled ? undefined : href}
        style={style}
        tabIndex={tabIndex}
        target={target}
        theme={_theme}
        tooltip={tooltip}
        type={type}
        width={width}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <ButtonContainer {...styleProps}>
          <IconEventWrapper
            fitParent
            activeOnHover={!disabled}
            theme={{
              color: _theme.iconColor || _theme.color,
              disabledColor: _theme.iconDisabledColor || _theme.disabledColor,
              activeColor: _theme.iconActiveColor || _theme.activeColor,
              activeDefaultColor:
                _theme.iconActiveDefaultColor || _theme.activeColor,
              secondaryColor:
                _theme.iconSecondaryColor || _theme.secondaryColor,
              secondaryActiveColor: _theme.iconSecondaryActiveColor,
            }}
          >
            <Content {...styleProps}>
              {/* InputContent is just a container for 'inputted' content,
               *  meaning label and icon. It's convenient for spacing.
               */}
              <InputContent {...styleProps}>
                {icon && !appendIcon && renderIcon()}
                {label && (
                  <Label $isActive={isActive} $theme={_theme}>
                    {label}
                  </Label>
                )}
                {icon && appendIcon && renderIcon()}
              </InputContent>
              {hasCaret && renderCaret()}
            </Content>
          </IconEventWrapper>
        </ButtonContainer>
      </BaseButton>
    );
  }
);

Button.displayName = 'Button';

export default withEventCatcher(Button);
